import { useState } from "react";
import { TrendingUp, TrendingDown, BarChart3, LayoutGrid, List } from 'lucide-react';
import { formatCurrency, formatNumber } from "./api.js";
import { SectionSkeleton, EmptyState, ApiError, DataTable, DonutChartWithTooltip } from '../utils/analyticsHelpers';

export default function SalesByChannel({ query }) {
  const [showMonthly, setShowMonthly] = useState(false);
  const [metric, setMetric] = useState("revenue");

  if (query.loading) return <SectionSkeleton withPie />;
  if (query.error) return <ApiError error={query.error} label="Sales by Channel" />;
  
  const data = query.data || {};
  const rows = data.rows || [];
  const totals = data.totals || {};
  const meta = data.meta || { ActualYear: 'Actual', lyYearLabel: 'LY', yblYearLabel: 'YBL' };

  if (!rows.length) return <EmptyState label="No sales found." />;

  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

  const TrendIndicator = ({ value }) => {
    if (value === undefined || value === null) return null;
    const isUp = value >= 0;
    return (
      <div className={`flex items-center gap-1 text-[10px] font-black ${isUp ? 'text-emerald-600' : 'text-rose-600'}`}>
        {isUp ? <TrendingUp size={12} strokeWidth={3} /> : <TrendingDown size={12} strokeWidth={3} />}
        <span>{isUp ? '+' : ''}{value}%</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2">
          {showMonthly && (
             <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                {['revenue', 'orders', 'units'].map(m => (
                  <button key={m} onClick={() => setMetric(m)} className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-md transition-all ${metric === m ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}>{m}</button>
                ))}
             </div>
          )}
        </div>
        <button onClick={() => setShowMonthly(!showMonthly)} className={`flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg border transition-all w-fit whitespace-nowrap ${showMonthly ? 'bg-slate-900 text-white border-slate-900 shadow-lg' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 shadow-sm'}`}>
          {showMonthly ? <List size={14} /> : <BarChart3 size={14} />}
          {showMonthly ? 'BACK' : 'MONTHLY BREAKDOWN'}
        </button>
      </div>

      {!showMonthly ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          <div className="flex flex-col gap-4 rounded-xl bg-white border border-slate-200 p-5 shadow-sm">
            <DonutChartWithTooltip data={rows.map(c => ({ label: c.channel, value: c.revenue, unitsSold: c.unitsSold, color: c.color }))} centerLabel="TOTAL" centerValue={formatCurrency(totals.revenue || 0)} />
            <div className="mt-4 border-t border-slate-100 pt-4 flex flex-col gap-3">
              {totals.history?.map((h, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl bg-slate-50 p-3 border border-slate-100">
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">{h.year} REVENUE</p>
                    <p className="text-sm font-black text-slate-800">{formatCurrency(h.revenue)}</p>
                  </div>
                  <TrendIndicator value={h.trend} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <DataTable
              columns={[
                { key: "channel", label: "CHANNEL" },
                { key: "orders", label: "ORDERS" },
                { key: "revenue", label: "REVENUE HISTORY", align: "right" },
                { key: "trend", label: "TREND", align: "right" },
                { key: "unitsSold", label: "UNITS SOLD", align: "right" },
              ]}
              rows={rows.map(c => ({
                channel: <div className="flex items-center gap-2 font-black text-slate-700 uppercase"><span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.channel}</div>,
                orders: formatNumber(c.orders),
                revenue: (
                  <div className="flex flex-col items-end gap-1.5 min-w-[160px] py-1">
                    <div className="flex items-center justify-between w-full">
                       <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Actual</span>
                       <span className="font-black text-slate-900">{formatCurrency(c.revenue)}</span>
                    </div>
                    <div className="w-full h-[1px] bg-slate-100"></div>
                    <div className="flex items-center justify-between w-full gap-2">
                       <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">VS {meta.lyYearLabel}</span>
                       <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-slate-600">{formatCurrency(c.lyRevenue)}</span>                          
                       </div>
                    </div>
                    <div className="flex items-center justify-between w-full gap-2">
                       <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">VS {meta.yblYearLabel}</span>
                       <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-slate-600">{formatCurrency(c.yblRevenue)}</span>                          
                       </div>
                    </div>
                  </div>
                ),
                trend: 
                <div className="flex flex-col items-end gap-1.5 min-w-[40px] py-1">
                  <div className="flex items-center justify-between w-full gap-2">&nbsp;</div>
                  <div className="flex items-center justify-between">
                    <TrendIndicator value={c.trend} />
                  </div>
                  <div className="flex items-center justify-between">
                     <TrendIndicator value={c.trendYBL} /></div>
                  </div>,
                unitsSold: formatNumber(c.unitsSold),
              }))}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="bg-slate-900 p-4 border-b border-slate-800">
            <h3 className="text-white font-black text-[11px] uppercase tracking-[0.25em] flex items-center gap-2"><LayoutGrid size={16} className="text-indigo-400" />MONTHLY BREAKDOWN MATRIX</h3>
          </div>
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full border-collapse min-w-[1200px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-4 text-left text-[10px] font-black text-slate-400 uppercase sticky left-0 bg-slate-50 z-20 border-r border-slate-200">CHANNELS</th>
                  {months.map(m => <th key={m} className="p-4 text-center text-[10px] font-black text-slate-400 uppercase border-r border-slate-100">{m}</th>)}
                  <th className="p-4 text-right text-[10px] font-black text-indigo-600 uppercase bg-indigo-50/50">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={rIdx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-black text-[11px] text-slate-700 sticky left-0 bg-white z-10 border-r border-slate-200 uppercase">
                      <div className="flex items-center gap-2 uppercase"><span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: row.color }} />{row.channel}</div>
                    </td>
                    {row.monthlyBreakdown.map((m, mIdx) => {
                       const mVal = metric === 'revenue' ? m.revenue : m[metric];
                       const mLYVal = metric === 'revenue' ? m.lyRevenue : m[`ly${metric.charAt(0).toUpperCase() + metric.slice(1)}`];
                       const mTrend = metric === 'revenue' ? m.revTrend : m[`${metric}Trend`];
                       return (
                        <td key={mIdx} className="p-3 text-center border-r border-slate-50">
                          <div className="flex flex-col items-center">
                            <span className="text-[11px] font-black text-slate-900">{metric === 'revenue' ? formatCurrency(mVal) : formatNumber(mVal)}</span>
                            <span className="text-[9px] font-bold text-slate-400 mt-0.5">{meta.lyYearLabel}: {metric === 'revenue' ? formatCurrency(mLYVal) : formatNumber(mLYVal)}</span>
                            <TrendIndicator value={mTrend} />
                          </div>
                        </td>
                       );
                    })}
                    <td className="p-4 text-right bg-slate-50/30 font-black text-slate-900 text-[11px] border-l border-slate-200">
                       <div className="flex flex-col items-end">
                         <span>{metric === 'revenue' ? formatCurrency(row.revenue) : formatNumber(row[metric === 'orders' ? 'orders' : 'unitsSold'])}</span>
                         <TrendIndicator value={row.trend} />
                       </div>
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-900 text-white font-black">
                  <td className="p-4 text-[10px] uppercase tracking-widest sticky left-0 bg-slate-900 z-10 border-r border-slate-800">MATRIX TOTAL</td>
                  {months.map((_, mIdx) => {
                    const colTotal = rows.reduce((s, r) => s + r.monthlyBreakdown[mIdx][metric], 0);
                    const lyKey = `ly${metric.charAt(0).toUpperCase() + metric.slice(1)}`;
                    const lyColTotal = rows.reduce((s, r) => s + (r.monthlyBreakdown[mIdx][lyKey] || 0), 0);
                    const colTrend = lyColTotal > 0 ? +(((colTotal - lyColTotal) / lyColTotal) * 100).toFixed(1) : 0;
                    return (
                      <td key={mIdx} className="p-4 text-center border-r border-slate-800">
                        <div className="flex flex-col items-center">
                          <span className="text-[11px]">{metric === 'revenue' ? formatCurrency(colTotal) : formatNumber(colTotal)}</span>
                          <TrendIndicator value={colTrend} />
                        </div>
                      </td>
                    );
                  })}
                  <td className="p-4 text-right text-[11px] bg-indigo-600 border-l border-indigo-700">{metric === 'revenue' ? formatCurrency(totals.revenue) : formatNumber(totals[metric === 'orders' ? 'orders' : 'unitsSold'])}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}